/**
 * Group-transfer session state — consolidates 13 session-keyed maps from
 * `MobyNode` into a single object per group transfer session.
 *
 * Why this exists:
 *   Group-transfer state was historically grown ad-hoc — each new feature
 *   (seeder discovery, retry epochs, OPFS backpressure, stagger, ACK tracking)
 *   added a fresh `Map<sessionId, …>` to `MobyNode`. By the time the snowball
 *   seeding work landed, 13 separate maps tracked overlapping aspects of the
 *   same session lifecycle. Symptoms:
 *
 *     1. Memory pressure — N sessions × 13 Maps × bucket overhead, plus
 *        duplicated state (`groupSeederDirectory` mirrored
 *        `record.metadata.groupSeeders`).
 *     2. Cleanup-path fragility — every detach/abort path has to remember to
 *        touch every map. The seat-starvation bug fixed in
 *        `group-transfer-eviction.ts` was a missed map in one such path.
 *     3. Cross-session collisions — `activeGroupServes` keys
 *        `${requesterAddr}:${chunkIdx}` with no sessionId. Two sessions
 *        requesting the same chunk index from the same peer collide.
 *     4. State invariants are implicit — no single object owns "the session";
 *        invariants like "every inflight chunk has an epoch" are scattered
 *        across the call graph.
 *
 *   This module replaces the maps with a single `GroupTransferSession` per
 *   sessionId, and a `GroupTransferRegistry` that owns their lifetime. The
 *   refactor is staged across multiple commits — each step migrates one map
 *   at a time, behavior-preserving except where Option B (per-session keying
 *   for active serves) intentionally fixes the collision noted above.
 *
 *   Phase 1 is a pure-state refactor. Role-aware topology (`hub`/`branch`/
 *   `leaf`), `GROUP_CHUNK_DEFER`, and per-flow bandwidth accounting all land
 *   in Phase 2/3 on top of this foundation. See
 *   `docs/group-transfer-optimization.md` for the multi-phase plan.
 *
 *   Run via `bun test src/lib/group-transfer-session.test.ts` once the test
 *   file is added in step 5.
 */

import type { FileTransferRecord } from './file-transfer-types';

// ─── Structural value types (mirrored from existing MobyNode maps) ───────────
//
// These intentionally avoid importing from `provider.tsx` to keep the module
// dependency-free. The shapes match what the original `Map` value types
// declared so production callers can assign without conversion.

/**
 * Per-chunk inflight tracking on the receiver side. Mirrors the value shape
 * of the original `groupPullInflight` map.
 */
export interface GroupChunkInflight {
  /** Address of the peer this chunk was requested from. */
  source: string;
  /** Wall-clock ms when the *current* request was sent (refreshed on sticky-extend). */
  requestedAt: number;
  /** Wall-clock ms when the chunk was *first* requested (immutable across stickies). */
  firstRequestedAt: number;
  /** Symbol count observed at the previous staleness check (sticky decision input). */
  lastSymbolCount: number;
  /** RaptorQ retry epoch — incremented on force-evict to invalidate prior decoders. */
  retryEpoch: number;
}

/**
 * Seeder availability record. Mirrors `groupSeederDirectory` value shape.
 */
export interface GroupSeederInfo {
  address: string;
  completedAt: number;
  availableChunks: number[];
}

/**
 * Sender-side serve abort signal. Mirrors `activeGroupServes` value shape.
 *
 * The serve loop in `serveGroupChunkToPeer` polls `aborted` between symbol
 * bursts. ACK arrival, epoch retry, and peer eviction all set `aborted=true`.
 * `nonce` distinguishes serves of the same chunk across retry epochs.
 */
export interface GroupServeSignal {
  aborted: boolean;
  nonce: number;
  /**
   * Set to true when `activeServeSeats` has already been decremented for
   * this serve. Prevents double-release when both the ACK handler and the
   * `finally` block in `serveGroupChunkToPeer` try to clean up.
   *
   * Tracked here so callers don't need to mutate via `(signal as any).…`
   * the way the pre-refactor code did.
   */
  refCountReleased?: boolean;
}

/**
 * C5B stagger offset cache. Each receiver pulls from a unique band of the
 * file so partial seeders rapidly form a disjoint chunk-set mesh.
 */
export interface GroupStaggerOffset {
  /** First file-chunk index this receiver targets (0-based, +1 for Merkle tree). */
  offset: number;
  /** Receiver count at the time the offset was computed (cache key). */
  receiverCount: number;
}

// ─── Session object ──────────────────────────────────────────────────────────

/**
 * All per-session group-transfer state, owned by `GroupTransferRegistry`.
 *
 * Field naming follows the pattern: drop the `group` prefix from the old map
 * names since the enclosing class already establishes the namespace
 * (`session.pullInflight` reads cleaner than `session.groupPullInflight`).
 *
 * Lifetime: created on first reference (sender publishes / receiver receives
 * `GROUP_FILE_METADATA`), retained while the underlying transfer is active,
 * disposed when the transfer reaches `COMPLETED` or `CANCELED`.
 */
export class GroupTransferSession {
  // ─── Identity ──────────────────────────────────────────────────────────────

  readonly sessionId: string;
  readonly rootHash: string;

  /**
   * Original sender (the "hub" in CDN terminology). Always has every chunk.
   * In phase 2 this drives the snowball penalty asymmetry; in phase 1 it's
   * just metadata.
   */
  readonly senderAddress: string;

  /**
   * Group chat address. Used for membership lookups and seeder broadcasts.
   * `undefined` only during partially-initialized sessions; production
   * callers should treat undefined as a fatal invariant.
   */
  readonly groupAddress: string | undefined;

  /**
   * Reference to the canonical `FileTransferRecord`. The record holds
   * metadata, received-chunk tracking, and lifecycle state — the session
   * holds *transport-level* state (inflight, epochs, seeders, etc.) that
   * was previously scattered across the 13 maps.
   *
   * Both objects are kept because the record predates the session and is
   * used by non-group code paths (`activeTransfers`, `sendQueue`, UI).
   */
  readonly record: FileTransferRecord;

  // ─── Receiver-side: pull-window state ─────────────────────────────────────

  /** chunkIndex → inflight info. Was `groupPullInflight.get(sessionId)`. */
  readonly pullInflight: Map<number, GroupChunkInflight> = new Map();

  /** chunkIndex → retry epoch counter. Was `groupChunkRetryEpochs.get(sessionId)`. */
  readonly retryEpochs: Map<number, number> = new Map();

  /** Stagger offset cache. Was `groupPullStaggerOffset.get(sessionId)`. */
  staggerOffset: GroupStaggerOffset | null = null;

  /**
   * Periodic redemption-loop interval handle. Was `groupRedemptionLoops`.
   * `null` when the loop is not running.
   */
  redemptionLoopTimer: number | null = null;

  // ─── Discovery (both sides) ───────────────────────────────────────────────

  /** seederAddress → availability info. Was `groupSeederDirectory.get(sessionId)`. */
  readonly seederDirectory: Map<string, GroupSeederInfo> = new Map();

  /**
   * Debounce timer for partial-seeder announce broadcasts. Was
   * `groupSeederAnnounceTimers.get(sessionId)`.
   */
  seederAnnounceTimer: number | null = null;

  /**
   * Most recently announced chunk count — guards against re-announcing the
   * same partial state. Was `groupSeederLastAnnouncedCount.get(sessionId)`.
   */
  seederLastAnnouncedCount: number = 0;

  // ─── Sender-side: serve tracking ──────────────────────────────────────────
  //
  // OPTION B FIX: `activeServes` is now keyed by `${requesterAddr}:${chunkIdx}`
  // *within this session*. The pre-refactor `activeGroupServes` map was node-
  // level and collided across sessions when the same peer requested the same
  // chunk index in two simultaneous group transfers. Per-session keying is
  // strictly more correct and was an explicit decision (see
  // `docs/group-transfer-optimization.md`).

  /** `${requesterAddr}:${chunkIdx}` → abort signal. Per-session keyed. */
  readonly activeServes: Map<string, GroupServeSignal> = new Map();

  /**
   * requesterAddr → count of in-flight serves to that peer for this session.
   * Drives the per-peer concurrency cap (`MAX_CONCURRENT_SERVES_PER_PEER`).
   * Was `activeGroupServeRefCount` (which mixed all sessions).
   */
  readonly activeServeSeats: Map<string, number> = new Map();

  // ─── Sender-side: ACK + readiness tracking ────────────────────────────────

  /** chunkIndex → set of receiver addresses that have ACKed. Was `groupFileAcks`. */
  readonly chunkAcks: Map<number, Set<string>> = new Map();

  /** Set of receivers that have sent `GROUP_FILE_READY`. Was `groupFileReadyReceivers`. */
  readonly readyReceivers: Set<string> = new Set();

  /**
   * receiverAddress → number of chunks ACKed by that receiver. Used for
   * O(1) UI progress updates without scanning `chunkAcks`. Was previously
   * stored on `record.metadata.groupReceiverProgress`; lifted here so it
   * can be cleaned up alongside the rest of the session state.
   */
  readonly receiverProgress: Map<string, number> = new Map();

  // ─── OPFS write tracking (rootHash-keyed today, 1:1 with session) ─────────

  /**
   * Number of file chunks confirmed flushed to OPFS. Used by the completion
   * drain in `processGroupChunkComplete`. Was `groupOpfsWritesDone[rootHash]`.
   */
  opfsWritesDone: number = 0;

  /**
   * Tail of the OPFS write queue for serializing concurrent writes. Was
   * `groupOpfsWriteQueues[rootHash]`. `null` until the first write enqueues.
   */
  opfsWriteQueueTail: Promise<void> | null = null;

  // ─── Construction ─────────────────────────────────────────────────────────

  constructor(args: {
    sessionId: string;
    rootHash: string;
    senderAddress: string;
    groupAddress: string | undefined;
    record: FileTransferRecord;
  }) {
    this.sessionId = args.sessionId;
    this.rootHash = args.rootHash;
    this.senderAddress = args.senderAddress;
    this.groupAddress = args.groupAddress;
    this.record = args.record;
  }

  // ─── Lifecycle helpers ────────────────────────────────────────────────────

  /**
   * Tear down all timers held by this session. Caller is responsible for
   * removing the session from the registry afterward.
   */
  dispose(): void {
    if (this.seederAnnounceTimer !== null) {
      clearTimeout(this.seederAnnounceTimer);
      this.seederAnnounceTimer = null;
    }
    if (this.redemptionLoopTimer !== null) {
      clearInterval(this.redemptionLoopTimer);
      this.redemptionLoopTimer = null;
    }
    // Maps and sets are dropped with the object reference.
  }
}

// ─── Registry ────────────────────────────────────────────────────────────────

/**
 * Owns all live `GroupTransferSession` objects on a node. Replaces the
 * scattered `Map<sessionId, …>` accessors on `MobyNode`.
 *
 * Held as `node.groupSessions` once Step 2 of the migration lands. During
 * the staged migration, individual fields are filled in step-by-step while
 * the legacy maps are deleted; the registry exists from Step 1 onward so
 * each migration step has a target.
 */
export class GroupTransferRegistry {
  private readonly sessions = new Map<string, GroupTransferSession>();

  /** Direct lookup. Returns `undefined` for unknown sessions. */
  get(sessionId: string): GroupTransferSession | undefined {
    return this.sessions.get(sessionId);
  }

  /** Idempotent insert. Returns existing session if one was already registered. */
  getOrCreate(args: {
    sessionId: string;
    rootHash: string;
    senderAddress: string;
    groupAddress: string | undefined;
    record: FileTransferRecord;
  }): GroupTransferSession {
    const existing = this.sessions.get(args.sessionId);
    if (existing) return existing;
    const session = new GroupTransferSession(args);
    this.sessions.set(args.sessionId, session);
    return session;
  }

  /** True if the session is currently registered. */
  has(sessionId: string): boolean {
    return this.sessions.has(sessionId);
  }

  /**
   * Dispose timers and remove the session. No-op if the session was never
   * registered (matches the legacy `Map.delete` behavior).
   */
  delete(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) return false;
    session.dispose();
    return this.sessions.delete(sessionId);
  }

  /** Iterate live sessions. Order is insertion-order (Map semantics). */
  values(): IterableIterator<GroupTransferSession> {
    return this.sessions.values();
  }

  /** Iterate `[sessionId, session]` pairs. */
  entries(): IterableIterator<[string, GroupTransferSession]> {
    return this.sessions.entries();
  }

  /** Number of registered sessions. */
  get size(): number {
    return this.sessions.size;
  }
}
